import CardWrapper from '../../components/wrapper/card';
import { formatTimeUptoNow } from '../../helper';
import { handleNotificationOnClick } from '../../helper/notification_helper';
import { useNavigate } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Box, Button, CircularProgress, IconButton, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import { CalendarMonthOutlined, EmailOutlined, LeaderboardOutlined, MoreVertOutlined, PersonOutlined, PhoneOutlined } from '@mui/icons-material';
import { fetchAllNotificaitonsThunk, markReadUnreadNotificaitonThunk } from '../../redux/thunks/notification';
import { NotificaitonObject, NotificationReadUnreadParams } from '../../params/redux/notification';
import React, { useEffect, useState } from 'react';
import { resetNotificationState, toggleNotificationReadState } from '../../redux/slices/notification';
import { useDispatch, useSelector } from 'react-redux';

const NotificationsPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [tabValue, setTabValue] = useState(0);
    const [loadingNotifId, setLoadingNotifId] = useState<number | null>(null);
    const { currentUser, isSales } = useSelector((state: RootState) => state.app);
    const { notifications } = useSelector((state: RootState) => state.notification);
    const [menuState, setMenuState] = useState<{ anchorEl: HTMLElement | null; selectedNotifId: number | null }>({
        anchorEl: null,
        selectedNotifId: null
    });

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, notifId: number) => {
        setMenuState({ anchorEl: event.currentTarget, selectedNotifId: notifId });
    };

    const handleMenuClose = () => {
        if (loadingNotifId !== null) return;
        setMenuState({ anchorEl: null, selectedNotifId: null });
    };
    const markAllAsRead = async () => {
        const originalNotifications: NotificaitonObject[] = JSON.parse(JSON.stringify(notifications));
        try {
            originalNotifications.forEach((n) => {
                dispatch(toggleNotificationReadState({
                    notification_id: n.id,
                    is_read: 1
                }));
            });
            handleMenuClose();
            const data: NotificationReadUnreadParams = {
                notification_id: null,
                is_all: 1,
                is_read: 1
            };
            await dispatch(markReadUnreadNotificaitonThunk(data)).unwrap();
        } catch (e) {
            dispatch(resetNotificationState(originalNotifications));
        }
    };


    useEffect(() => {
        const fetchNotificaions = async () => {
            try {
                await dispatch(fetchAllNotificaitonsThunk()).unwrap();
            } catch (e) {
                //
            }
        };
        fetchNotificaions();
        const interval = setInterval(() => {
            // fetch Notifications after 30 seconds
            fetchNotificaions();
        }, 30000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    const unreadCount = notifications.filter((notif: NotificaitonObject) => notif.is_read === 0).length;

    // Filtered notifications based on tab selection
    const filteredNotifications = tabValue === 0 ? notifications : notifications.filter((notif: NotificaitonObject) => notif.is_read === 0);

    const selectedNotificationHandler = async (notification: NotificaitonObject) => {
        const originalNotifications: NotificaitonObject[] = JSON.parse(JSON.stringify(notifications));
        try {
            dispatch(toggleNotificationReadState({
                notification_id: notification.id,
                is_read: notification.is_read === 1 ? 0 : 1
            }));
            handleMenuClose();
            const data: NotificationReadUnreadParams = {
                notification_id: notification.id,
                is_all: 0,
                is_read: notification.is_read === 1 ? 0 : 1
            };
            await dispatch(markReadUnreadNotificaitonThunk(data)).unwrap();
        } catch (e) {
            dispatch(resetNotificationState(originalNotifications));
        }
    };
    const onNotificationClick = async (notif: NotificaitonObject) => {
        if (loadingNotifId !== null) return;
        // start loading
        setLoadingNotifId(notif.id);
        try {
            // your existing helper does all the dispatches + navigate + close
            await handleNotificationOnClick(
                notif,
                dispatch,
                navigate,
                () => null,
                currentUser,
                isSales
            );
        } catch (e) {
            console.error('notification click failed', e);
        } finally {
            // stop loading
            setLoadingNotifId(null);
        }
    };

    return (
        <Box p={2}>
            <CardWrapper minHeight="85vh">
                <Typography variant="h5" fontWeight="bold" mb={2}>
                    Notifications
                </Typography>

                {/* Tabs for All & Unread */}
                <Box display="flex" justifyContent="space-between" alignItems="center"><Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                    <Tab label={`All notifications (${notifications.length})`} />
                    <Tab label={`Unread notifications (${unreadCount})`} />
                </Tabs>

                    {/* Mark All as Read */}
                    <Button
                        variant="text"
                        color="primary"
                        disabled={notifications !== null}
                        sx={{ cursor: 'pointer', textAlign: 'right', mb: 2 }}
                        onClick={markAllAsRead}
                    >
                        Mark all as read
                    </Button>
                </Box>

                {/* Notifications List */}
                <Box display="flex" flexDirection="column" gap={2} >
                    {filteredNotifications.length > 0
                        ? (
                            filteredNotifications.map((notif: NotificaitonObject) => (
                                <Box
                                    key={notif.id}
                                    bgcolor={notif.is_read === 1 ? 'transparent' : '#f5f5f5'}
                                    borderRadius={1}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    p={2}
                                >
                                    <Box display="flex" gap={2} alignItems="center"
                                        sx={{
                                            cursor: notif.type === 'general' ? 'initial' : 'pointer'
                                        }}
                                        onClick={() => onNotificationClick(notif)}>
                                        <Box><Avatar sx={{ bgcolor: 'lightgray' }} >
                                            {notif.type === 'lead'
                                                ? <LeaderboardOutlined />
                                                : notif.type === 'sms'
                                                    ? <PhoneOutlined />
                                                    : notif.type === 'email'
                                                        ? <EmailOutlined />
                                                        : notif.type === 'meeting_reminder'
                                                            ? <CalendarMonthOutlined />
                                                            : <PersonOutlined />}</Avatar></Box>
                                        <Box flexGrow={1}>
                                            <Typography variant="subtitle1">
                                                {notif.description}
                                            </Typography>
                                            {notif.created_at && formatTimeUptoNow(notif.created_at)}
                                        </Box>
                                    </Box>
                                    {
                                        loadingNotifId === notif.id
                                            ? <CircularProgress />
                                            : (
                                                <IconButton disabled={loadingNotifId !== null} onClick={(e) => handleMenuClick(e, notif.id)}>
                                                    <MoreVertOutlined />
                                                </IconButton>
                                            )
                                    }
                                    <Menu anchorEl={menuState.anchorEl} open={menuState.selectedNotifId === notif.id} onClose={handleMenuClose}>
                                        <MenuItem onClick={() => selectedNotificationHandler(notif)}>Mark as {notif.is_read === 1 ? 'unread' : 'read'}</MenuItem>
                                    </Menu>
                                </Box>
                            ))
                        )
                        : (
                            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
                                No notifications
                            </Typography>
                        )}
                </Box>

                {/* Three Dots Menu */}
            </CardWrapper>
        </Box>
    );
};

export default NotificationsPage;
